import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { PropsWithChildren, ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { makePopoutId } from './generateId';
import { openExternalWindow, OpenExternalWindowResponse } from './openExternalWindow';
import { PanelInstanceContext } from './PanelInstanceContext';
import { PanelPosition, PanelSizeExact } from './types';
import { useRestoreStateFrom, useSaveStateIn } from './usePanelStateHandler';

interface _PopoutWindowProps {
  name: string;
  title?: string; // The title of the popout window
  size: PanelSizeExact;
  popInPanelPosition: PanelPosition;
  popInPanelSize: PanelSizeExact;

  //says if we want to watch for any dynamic style changes
  //only needed when using UI libraries like MUI
  observeStyleChanges?: boolean;

  /** Testing seam to avoid extensively mocking the window object. */
  openExternalWindowFn?: () => OpenExternalWindowResponse;
  className?: string;
}

export type PopoutWindowProps = PropsWithChildren<_PopoutWindowProps>;

export const PopoutWindow = ({
  name,
  title = name,
  size,
  popInPanelPosition,
  popInPanelSize,
  observeStyleChanges,
  className,
  openExternalWindowFn,
  children,
}: PopoutWindowProps): ReactElement | null => {
  const { setPanelWindow } = useContext(PanelInstanceContext);

  const [containerElement, setContainerElement] = useState<HTMLElement>();
  const [headElement, setHeadElement] = useState<HTMLElement>();

  const savedState = useRestoreStateFrom({ uniqueId: name, panelPoppedOut: true });

  // If we use a popped in position as a popped out position we must adjust for which screen the window is on.
  const poppedInPosition = popInPanelPosition;
  const poppedInPositionAdjusted = {
    x: window.screenX + poppedInPosition.x,
    y: window.screenY + poppedInPosition.y,
  };
  const position = savedState?.panelPosition ?? poppedInPositionAdjusted;
  const panelSize = savedState?.panelSize ?? popInPanelSize ?? size;

  const saveStateIn = useSaveStateIn({ panelPoppedOut: true, uniqueId: name });

  // When we create this component, open a new window
  useEffect(
    () => {
      const oew = openExternalWindowFn ?? openExternalWindow;
      const { externalWindow, onCloseExternalWindow } = oew({
        title,
        size: panelSize.height === 'auto' ? { ...panelSize, height: 400 } : (panelSize as PanelSizeExact),
        setContainerElement,
        setHeadElement,
        observeStyleChanges,
        className,
        position,
      });

      if (!externalWindow) return;

      setPanelWindow(externalWindow);

      const closeExternalWindow = () => {
        externalWindow.close();
      };

      const savePanelState = () => {
        saveStateIn({
          panelPosition: {
            x: externalWindow.screenX,
            y: externalWindow.screenY,
          },
          panelSize: {
            height: externalWindow.innerHeight,
            width: externalWindow.innerWidth,
          },
        });
      };

      externalWindow.addEventListener('beforeunload', savePanelState);

      // When the main window closes or reloads, close the popout
      window.addEventListener('beforeunload', closeExternalWindow, { once: true });

      return () => {
        onCloseExternalWindow?.();
      };
    },
    // These deps MUST be empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Render this component's children into the root element of the popout window
  // The `@emotion/react/CacheProvider`provides a cache derived from createCache for inline styles
  // these will be generated into the `container` and prefixed with the `key` below, ensuring
  // the styles are in the popout rather than the root window.
  // Memoised so the cache (and its deduplication tracking) is stable across re-renders,
  // and only created once headElement is defined — if container is undefined Emotion falls
  // back to document.head, causing styles to be orphaned in the root window.
  const myCache = useMemo(
    () => (headElement ? createCache({ key: makePopoutId(name), container: headElement }) : null),
    [headElement, name],
  );

  if (!containerElement || !myCache) {
    return null;
  }

  return createPortal(<CacheProvider value={myCache}>{children}</CacheProvider>, containerElement);
};
